import { Injectable } from '@angular/core';
import { GmsContext } from '../models/gms/gms-context';
import { Engine } from '../models/gms/engine';
import { GmsContextService } from './gms-context.service';
import { GmsConnection } from '../models/gms/gms-connection-enum';
import { COMPACT_VIEW, HIGH_CONTRAST, LIGHT_MODE } from '../components/_shared/constants/local-storage-constants';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';
import { MessageType } from '../models/gms/message';

@Injectable({
  providedIn: 'root',
})
export class MainService {
  private gmsListInitializedSubject$ = new BehaviorSubject<boolean>(false);
  private isLightModeActiveSubject$ = new BehaviorSubject<boolean>(JSON.parse(localStorage.getItem(LIGHT_MODE) ?? JSON.stringify(false)));

  constructor(private readonly gmsContextService: GmsContextService) {}

  public initGmsList() {
    this.loadingGms = true;
    this.loadingFavorites = true;
    this.gmsContextService.getFavorites().subscribe((favorites) => {
      this.favoriteGmsList = favorites.sort((a, b) => a.sortOrder - b.sortOrder);
      this.loadingFavorites = false;
    });
    this.gmsContextService.getAll().subscribe((gmsList) => {
      this.gmsList = gmsList;
      this.loadingGms = false;
      this.gmsListInitializedSubject$.next(true);
    });
  }

  public menuExpanded = false;
  public loadingGms = false;
  public loadingFavorites = false;

  gmsList: GmsContext[] = [];
  favoriteGmsList: GmsContext[] = [];
  temporaryFavorite: GmsContext = null;

  activeItem: GmsContext = undefined;
  activeEngine: Engine = undefined;

  public get gmsListInitialized$(): Observable<boolean> {
    return this.gmsListInitializedSubject$.asObservable().pipe(distinctUntilChanged());
  }

  public get lightMode$(): Observable<boolean> {
    return this.isLightModeActiveSubject$.asObservable();
  }
  public setLightMode(lightMode: boolean) {
    localStorage.setItem(LIGHT_MODE, JSON.stringify(lightMode ?? false));
    this.isLightModeActiveSubject$.next(lightMode);
  }

  public get highContrast(): boolean {
    return JSON.parse(localStorage.getItem(HIGH_CONTRAST) ?? JSON.stringify(false));
  }
  public set highContrast(highContrast: boolean) {
    localStorage.setItem(HIGH_CONTRAST, JSON.stringify(highContrast ?? false));
  }

  public get compactView(): boolean {
    return JSON.parse(localStorage.getItem(COMPACT_VIEW) ?? JSON.stringify(false));
  }
  public set compactView(compactView: boolean) {
    localStorage.setItem(COMPACT_VIEW, JSON.stringify(compactView ?? false));
  }

  anomalyAmp5Data =
    // prettier-ignore
    // eslint-disable-next-line max-len
    [11.449834, 11.462236, 11.461101, 11.491662, 11.476654, 11.453204, 11.427336, 11.440214, 11.462569, 11.453222, 11.426681, 11.435103, 11.440335, 11.463814, 11.484296, 11.488277, 11.480869, 11.468478, 11.439352, 11.426156, 11.424127, 11.408369, 11.399124, 11.380799, 11.351477, 11.350148, 11.324345, 11.326217, 11.335339, 11.303914, 11.290925, 11.277993, 11.29097, 11.283494, 11.282229, 11.280926, 11.290715, 11.330206, 11.331589, 11.332506, 11.34703, 11.365957, 11.369219, 11.3926935, 11.383858, 11.407602, 11.4256315, 11.464267, 11.448229, 11.466875, 11.465983, 11.485759, 11.47816, 11.451176, 11.844587, 11.554735, 11.443649, 11.503195, 12.962853, 11.786539, 11.442064, '12,045', 11.4821615, 11.476224, 11.471138, 11.455656, 11.462405, 11.443062, 11.411693, 11.412015, 11.392778, 11.388717, 11.346498, 11.357361, 11.324827, 11.302749, 11.310007, 11.297262191772461, 11.30291, 11.287612, 11.276878, 11.305254, 11.293218, 11.268958, 11.28878, 11.296048, 11.32343, 11.319777, 11.3403, 11.353047, 11.378271, 11.379838, 11.390706, 11.421181, 11.411064, 11.447144, 11.445672, 11.467447, 11.461898, 11.468041, 11.530781, 11.46688, 11.427131, 11.435279, 11.440696, 11.436674, 11.448245, 11.419218, 11.425262, 11.469455, 11.471702, 11.474287, 11.458927, 11.473388, 11.441803, 11.454503, 11.43803, 11.401437, 11.392986, 11.368515, 11.360407, 11.360277, 11.350227, 11.329736, 11.305769, 11.29544, 11.28918, 11.286381, 11.288452, 11.294304, 11.311404, 11.29422, 11.303111, 11.303025, 11.351173, 11.337085, 11.343016, 11.3532915, 11.372866, 11.404156, 11.40992, 11.427243, 11.441926, 11.455745, 11.449088, 11.463261, 11.472393, 11.475513, 11.457021, 11.446483, 11.428953, 11.426033, 11.465741, 11.445617, 11.434988, 11.444886, 11.448434, 11.465104, 11.47868, 11.466774, 11.473172, 11.46158, 11.4437895, 11.44021, 11.413364, 11.403291, 11.379103, 11.386655, 11.373133, 11.348523, 11.329307, 11.324537, 11.283392, 11.272935, 11.31178, 11.30079, 11.297653, 11.2718725, 11.2836275, 11.279619, 11.287985, 11.300809, 11.299216, 11.337289, 11.325109, 11.3547, 11.382401, 11.380719, 11.414083, 11.439147, 11.440574, 11.455836, 11.443211, 11.455508, 11.461901, 11.486758, 11.470463, 11.461583, 11.438985, 11.440654, 11.440932, 11.429177, 11.436865, 11.431166, 11.418056, 11.473006, 11.481341, 11.486591, 11.484565, 11.4632, 11.449907, 11.444527, 11.430678, 11.378348, 11.396166, 11.377816, 11.371041, 11.365581, 11.337559, 11.322679, 11.319019, 11.3093405, 11.308082, 11.292562, 11.280055, 11.305761, 11.303668, 11.287472, 11.282682, 11.295087, 11.335758, 11.348196, 11.344056, 11.365232, 11.359919, 11.382765, 11.414745, 11.407955, 11.441886, 11.428185, 11.454739, 11.448696, 11.46752, 11.48409, 11.476228, 11.425626, 11.4560995, 11.442831, 11.445303, 11.442002, 11.426617, 11.453507, 11.436122, 11.442363, 11.477394, 11.476784, 11.487742, 11.466561, 11.452518, 11.461875, 11.410209, 11.404905, 11.397287, 11.379288, 11.366963, 11.348255, 11.336937, 11.30964, 11.3044815, 11.308465, 11.281971, 11.291618, 11.298677, 11.312424, 11.305254, 11.308716, 11.296803, 11.296237, 11.3197365, 11.317271, 11.3466215, 11.386204, 11.368004, 11.377842, 11.399529, 11.433684, 11.445884, 11.421163, 11.451365, 11.468873, 11.480007, 11.490522, 11.469723, 11.456493, 11.44336, 11.426174, 11.426581, 11.441987, 11.438162, 11.431425, 11.4338, 11.472713, 11.46897, 11.460947, 11.471165, 11.456377, 11.454624, 11.444223, 11.410831, 11.405062, 11.3897085, 11.3736725, 11.352417, 11.3629675, 11.326395, 11.313243, 11.295418, 11.293115, 11.290394, 11.279541, 11.2818165, 11.29473, 11.290061, 11.295379638671875, 11.3030615, 11.29237, 11.325446, 11.31749, 11.340242, 11.35372, 11.369512, 11.379747, 11.418379, 11.395591, 11.430268, 11.446207, 11.449459, 11.466723, 11.471125, 11.486953, 11.485724, 11.459621, 11.440391, 11.438363, 11.436003, 11.43132, 11.433271, 11.428723, 11.437186, 11.465113, 11.483158, 11.495488, 11.469577, 11.466154, 11.445383, 11.436925, 11.419298, 11.396751, 11.397019, 11.407654, 11.363585, 11.363641, 11.32837, 11.318339, 11.31666, 11.294906, 11.282465, 11.28605, 11.309136, 11.298345, 11.301554, 11.2772875, 11.287374, 11.291824];

  anomalyIm5Data =
    // prettier-ignore
    // eslint-disable-next-line max-len
    [11.364182, 11.369744, 11.357043, 11.367689, 11.337185, 11.28485, 11.237937, 11.236354, 11.25893, 11.253465, 11.22951, 11.236487, 11.247426, 11.314716, 11.370437, 11.3850155, 11.387032, '11,386', 11.371721, 11.364077, 11.368839, 11.35737, 11.355503, 11.337308, 11.313983, 11.314389, 11.294473, 11.298753, 11.3077, 11.279458, 11.266489, 11.2533245, 11.2681675, 11.257653, 11.257554, 11.258101, 11.265988, 11.301168, 11.302614, 11.298637, 11.309239, 11.32393, 11.319609, 11.341266, 11.326345, 11.348392, 11.354774, 11.389625, 11.362288, 11.363085, 11.35349, 11.355387, 11.329962, 11.222339, 11.615566, 11.36063, 11.243126, 11.422896, 11.290958, 11.58335, 11.262811, 11.479195, 11.363925, 11.377959, 11.379686, 11.372574, 11.39054, 11.381747, 11.35647, 11.3619585, 11.350554, 11.344927, 11.311826, 11.322848, 11.293788, 11.276063, 11.284639, 11.272482872009277, 11.279191, 11.265853, 11.253271, 11.282117, 11.269197, 11.2445965, 11.260755, 11.266936, 11.288964, 11.286097, 11.303131, 11.311098, 11.330603, 11.329884, 11.332213, 11.35776, 11.341081, 11.366568, 11.358015, 11.371182, 11.354514, 11.34288, 11.373218, 11.291479, 11.240795, 11.235197, 11.2409935, 11.241992, 11.245313, 11.217164, 11.257711, 11.337273, 11.3584795, 11.373152, 11.367182, 11.395672, 11.372735, 11.391729, 11.380881, 11.351013, 11.345901, 11.326319, 11.323771, 11.328134, 11.320935, 11.304028, 11.278748, 11.270553, 11.266895, 11.260584, 11.266249, 11.2732, 11.289409, 11.270907, 11.276574, 11.272368, 11.3206415, 11.303554, 11.304419, 11.3111925, 11.325711, 11.355948, 11.353616, 11.364863, 11.3685875, 11.382339, 11.362015, 11.3665085, 11.363546, 11.3491, 11.319417, 11.275344, 11.237584, 11.224169, 11.266252, 11.245897, 11.236569, 11.242675, 11.251698, 11.331622, 11.37266, 11.37029, 11.382066, 11.376409, 11.378037, 11.378762, 11.358769, 11.358014, 11.337221, 11.346563, 11.333918, 11.312181, 11.295861, 11.295553, 11.257214, 11.252133, 11.288024, 11.276794, 11.273679, 11.251948, 11.259709, 11.257107, 11.259947, 11.273508, 11.270933, 11.301679, 11.288607, 11.310346, 11.33376, 11.334407, 11.355556, 11.378253, 11.36826, 11.377903, 11.358788, 11.359442, 11.353298, 11.360347, 11.330076, 11.296449, 11.253473, 11.240543, 11.239389, 11.226233, 11.237012, 11.234758, 11.243643, 11.322165, 11.366787, 11.381335, 11.39653, 11.380335, 11.382091, 11.379744, 11.373516, 11.33109, 11.353449, 11.341366, 11.333122, 11.33169, 11.307788, 11.295063, 11.293522, 11.286728, 11.284143, 11.269567, 11.255987, '11,282', 11.279136, 11.262704, 11.258591, 11.265122, 11.305254, 11.314191, 11.303787, 11.325578, 11.313395, 11.332618, 11.356657, 11.34918, 11.37131, 11.355341, 11.371782, 11.349445, 11.355811, 11.359792, 11.33704, 11.24919, 11.266654, 11.24515, 11.245249, 11.246106, 11.226865, 11.256872, 11.268993, 11.301493, 11.364833, 11.373471, 11.403457, 11.384546, 11.384055, 11.404357, 11.354423, 11.35763, 11.351973, 11.335422, 11.331263, 11.312914, 11.30732, 11.281351, 11.27743, 11.284998, 11.258991, 11.266489, 11.277249, 11.290012, 11.280746, 11.284352, 11.271138, 11.266169, 11.286977, 11.283849, 11.308884, 11.343416, 11.324351, 11.328316, 11.343126, 11.370093, 11.374411, 11.344808, 11.36803, 11.369799, 11.372797, 11.360057, 11.333176, 11.279966, 11.242735, 11.229022, 11.225264, 11.245839, 11.23907, 11.234605, 11.239805, 11.336856, 11.352653, 11.3635235, 11.373419, 11.382783, 11.387755, 11.3827505, 11.359509, 11.359366, 11.342261, 11.332322, 11.320272, 11.330736, 11.295042, 11.285549, 11.270209, 11.267969, 11.264412, 11.2555895, 11.260386, 11.271317, 11.2667265, 11.275274276733398, 11.277297, 11.263091, 11.295503, 11.280784, 11.302794, 11.315393, 11.324014, 11.327754, 11.359497, 11.3349, 11.360536, 11.366681, 11.366049, 11.375723, 11.361348, 11.354289, 11.338281, 11.288774, 11.241728, 11.240667, 11.240322, 11.229861, 11.240148, 11.230472, 11.261633, 11.328976, 11.368839, 11.397854, 11.381239, 11.389941, 11.379984, 11.373132, 11.365947, 11.344316, 11.353459, 11.368422, 11.328765, 11.328849, 11.297527, 11.289018, 11.291348, 11.271549, 11.260615, 11.263209, 11.286847, 11.276369, 11.2771845, 11.25328, 11.26187, 11.263732];

  public chartApp1: any = {
    id: 'chartApp1',
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      height: 170,
      width: 340,
    },
    title: {
      text: 'Status<br>Free end total',
      align: 'center',
      verticalAlign: 'middle',
      y: 70,
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
    },
    accessibility: {
      point: {
        valueSuffix: '%',
      },
    },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: true,
          distance: -30,
          style: {
            fontWeight: 'bold',
            color: 'white',
          },
        },
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '130%'],
        size: '275%',
      },
    },
    series: [
      {
        type: 'pie',
        title: 'Status',
        innerSize: '60%',
        data: [
          ['good', 85.68],
          ['warning', 3.29],
          ['alarm', 3.42],
          {
            title: 'uncategorized',
            y: 7.61,
            dataLabels: { enabled: false },
          },
        ],
      },
    ],
  };

  static combineTimeData(dates: Date[], y: number[]) {
    if (!dates || dates.length < 1) {
      return [];
    }
    return MainService.combineData(
      dates.map((d) => new Date(d).getTime()),
      y
    );
  }

  static combineData(x: number[], y: number[]) {
    if (x.length !== y.length) {
      return;
    }

    const data = [];
    for (let i = 0; i < x.length; i++) {
      // GMS-546 filter 10^10 for all apps in the dashboard
      if (y[i] === 10000000000) {
        data.push([x[i], undefined]);
      } else {
        data.push([x[i], y[i]]);
      }
    }

    return data;
  }

  timeUntil(date: Date, type?: string) {
    if (!date) {
      return ['NaN', 'NaN'];
    }

    date = new Date(date);

    let seconds = date.getTime();
    if (type === 'future') {
      seconds = (seconds - new Date().getTime()) / 1000;
    } else if (type === 'time' || type === 'hours') {
    } else {
      seconds = (new Date().getTime() - seconds) / 1000;
    }
    let interval = seconds / 86400;
    if (type !== 'hours') {
      if (interval >= 2) {
        return [Math.round(interval), 'days'];
      }
      if (interval >= 1) {
        return [Math.round(interval), 'day'];
      }
    }

    interval = seconds / 3600;
    if (interval >= 2) {
      return [Math.round(interval), 'hours'];
    }
    if (interval >= 1) {
      return [Math.round(interval), 'hour'];
    }
    interval = seconds / 60;
    if (interval >= 2) {
      return [Math.round(interval), 'minutes'];
    }
    if (interval >= 1) {
      return [Math.round(interval), 'minute'];
    }

    interval = seconds;
    if (interval <= 1) {
      return [Math.round(interval), 'second'];
    }

    return [Math.round(interval), 'seconds'];
  }

  getConnectionDescription(connectionQuality: number, cloudPushType): GmsConnection {
    if (cloudPushType === 'manualPush') {
      return GmsConnection.UPLOAD;
    } else if (connectionQuality >= 0.5) {
      return GmsConnection.STRONG;
    } else if (connectionQuality >= 0.15) {
      return GmsConnection.GOOD;
    } else if (connectionQuality >= 0.05) {
      return GmsConnection.OK;
    } else if (connectionQuality > 0.0) {
      return GmsConnection.WEAK;
    } else {
      return GmsConnection.NO_SIGNAL;
    }
  }

  setActiveById(itemId: string, engineIndex?: number): void {
    // console.log('setActiveById: itemId = ' + itemId + ' , engineIndex = ' + engineIndex);
    // console.log(this.activeItem);
    // console.log(this.gmsList);
    if (!this.activeItem || this.activeItem.gmsId !== itemId || this.activeEngine?.gmsId !== itemId || this.activeEngine?.engineIndex !== engineIndex) {
      this.gmsList.forEach((i) => {
        if (i.gmsId === itemId) {
          this.activeItem = i;
          if (engineIndex !== undefined && i.statusPerEngine) {
            i.statusPerEngine.forEach((e) => {
              if (e.engineIndex === engineIndex) {
                this.activeEngine = e;
                this.activeEngine.gmsId = this.activeItem.gmsId;
                // console.log('updated activeEngine');
                // console.log(this.activeEngine);
              }
            });
          }
        }
      });
    }
  }
}

export interface MessageElement {
  nr: number;
  sender: string;
  messageType: MessageType;
  shortTitle: string;
  date: Date;
  longTitle: string;
  description: string;
  read: boolean;
}
